import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Publication} from '../types/Publication';
import publicationsService from '../services/publicationsService';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {
  AdminButton,
  AdminForm,
  AdminFormSection,
  AdminInputText,
  AdminInputYear,
  AdminTextArea
} from "../../../layout/admin";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const PublicationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    publicationDate: ''
  });
  const [publication, setPublication] = useState<Publication | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    if (isEditMode && id) {
      fetchPublication(id);
    }
  }, [id, isEditMode]);

  const fetchPublication = async (publicationId: string): Promise<void> => {
    setLoading(true);
    try {
      const publicationData = await publicationsService.getPublication(publicationId);
      setPublication(publicationData);
      setFormData({
        name: publicationData.name,
        title: publicationData.title,
        description: publicationData.description || '',
        publicationDate: publicationData.publicationDate || ''
      });
    } catch (error) {
      console.error('Error fetching publication:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      if (isEditMode && publication) {
        const updatedPublication: Publication = {
          id: publication.id,
          name: formData.name,
          title: formData.title,
          description: formData.description || undefined,
          publicationDate: formData.publicationDate || undefined
        };
        await publicationsService.updatePublication(updatedPublication);
      } else {
        await publicationsService.createPublication({
          name: formData.name,
          title: formData.title,
          description: formData.description || undefined,
          publicationDate: formData.publicationDate || undefined
        });
      }
      navigate('/publications');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AdminFormHeader
        title={publication ? t('admin.publications.title.edit') : t('admin.publications.title.create')}
        subtitle={publication ? t('admin.publications.subtitle.edit') : t('admin.publications.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                name="name"
                id="name"
                label={t('admin.common.fields.name')}
                required={true}
                error={errors.name}
              />

              <AdminInputText
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                name="title"
                id="title"
                label={t('admin.publications.fields.title')}
                required={true}
                error={errors.title}
              />

              <AdminInputYear
                value={formData.publicationDate}
                onChange={(e) => handleInputChange('publicationDate', e.target.value)}
                name="publicationDate"
                id="publicationDate"
                label={t('admin.publications.fields.publicationDate')}
              />

              <AdminTextArea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                name="description"
                id="description"
                label={t('admin.common.fields.description')}
                rows={5}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/publications")}
            />
            <AdminButton
              type="submit"
              label={publication ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default PublicationForm;

